import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import logoLong from '../../assets/diligenshub-logo.svg';
import { LOGIN_URL } from '../../lib/links';
import { ALL_USE_CASES, SOLUTION_GROUPS, SOLUTIONS_MENU, isSolutionsPath } from '../../content/solutions/nav';
import { RESOURCE_LINKS, RESOURCES_MENU, isResourcesPath } from '../../content/resources/nav';

const navLinks = [
  { name: 'Product', href: '/product' },
  { name: 'AI Features', href: '/ai-features' },
  { name: 'Security', href: '/security' },
  { name: 'How It Works', href: '/how-it-works' },
];

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Active page: navy underline on the header's bottom edge, the app's tab idiom.
const tabClass = (active) =>
  `inline-flex items-center whitespace-nowrap border-b-2 -mb-px text-sm font-medium transition-colors duration-150 ${
    active ? 'border-primary-900 text-primary-900' : 'border-transparent text-neutral-600 hover:text-primary-900'
  }`;

// A header menu (Solutions, Resources): opens on click (not hover), closes on
// Escape, a click outside, focus leaving it, or navigation. Its links follow
// the button in the tab order, so the keyboard reaches them once it's open.
// The header holds which menu is open, so opening one closes the other.
// `groups` are { label?, links }; a group label shows in the footer's
// column-heading style. `footer` is an optional link under a rule.
const NavMenu = ({ id, label, groups, footer, active, open, setOpen, pathname, panelClass }) => {
  const wrapper = useRef(null);
  const button = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onPointer = (event) => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, setOpen]);

  const onBlur = (event) => {
    if (event.relatedTarget && !wrapper.current?.contains(event.relatedTarget)) setOpen(false);
  };

  const linkClass = (to) =>
    `text-sm ${pathname === to ? 'font-medium text-primary-900' : 'text-neutral-600 hover:text-primary-900'}`;

  return (
    <div ref={wrapper} className="relative flex items-stretch" onBlur={onBlur}>
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        onClick={() => setOpen(!open)}
        className={`${tabClass(active)} gap-1`}
        data-testid={`nav-${id}-button`}
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          id={`${id}-menu`}
          className={`absolute left-1/2 top-full z-50 mt-px -translate-x-1/2 rounded border border-neutral-200 bg-white p-6 ${panelClass}`}
          data-testid={`${id}-menu`}
        >
          <div className={groups.length > 1 ? 'grid grid-cols-2 gap-8' : ''}>
            {groups.map((group, g) => {
              const labelId = group.label ? `${id}-${slug(group.label)}` : undefined;
              return (
                <div key={group.label || g}>
                  {group.label && (
                    <p className="label-uppercase mb-3 text-neutral-500" id={labelId}>
                      {group.label}
                    </p>
                  )}
                  <ul className="space-y-2" aria-labelledby={labelId}>
                    {group.links.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          aria-current={pathname === link.to ? 'page' : undefined}
                          className={linkClass(link.to)}
                          onClick={() => setOpen(false)}
                          data-testid={`${id}-link-${slug(link.label)}`}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          {footer && (
            <div className="mt-5 border-t border-neutral-200 pt-4">
              <Link
                to={footer.to}
                aria-current={pathname === footer.to ? 'page' : undefined}
                className="text-sm font-medium text-primary-900 hover:underline underline-offset-4"
                onClick={() => setOpen(false)}
                data-testid={`${id}-link-${slug(footer.label)}`}
              >
                {footer.label}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const [openMenu, setOpenMenu] = useState(null);

  const isActive = (href) => pathname === href;

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  // Which header menu is open: opening one closes the other.
  const menuState = (id) => ({
    open: openMenu === id,
    setOpen: (value) => setOpenMenu((current) => (value ? id : current === id ? null : current)),
  });

  const mobileLink = (to, label, testId) => (
    <Link
      key={to}
      to={to}
      aria-current={isActive(to) ? 'page' : undefined}
      className={`block py-2.5 text-sm font-medium ${isActive(to) ? 'text-primary-900' : 'text-neutral-600'}`}
      onClick={() => setMobileMenuOpen(false)}
      data-testid={testId}
    >
      {label}
    </Link>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-neutral-200" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={logoLong} alt="DiligensHub" className="h-7 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-stretch self-stretch space-x-5 xl:space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                aria-current={isActive(link.href) ? 'page' : undefined}
                className={tabClass(isActive(link.href))}
                data-testid={`nav-link-${slug(link.name)}`}
              >
                {link.name}
              </Link>
            ))}
            <NavMenu
              id="solutions"
              label={SOLUTIONS_MENU}
              groups={SOLUTION_GROUPS}
              footer={ALL_USE_CASES}
              active={isSolutionsPath(pathname)}
              pathname={pathname}
              panelClass="w-[30rem]"
              {...menuState('solutions')}
            />
            <NavMenu
              id="resources"
              label={RESOURCES_MENU}
              groups={[{ links: RESOURCE_LINKS }]}
              active={isResourcesPath(pathname)}
              pathname={pathname}
              panelClass="w-64"
              {...menuState('resources')}
            />
          </nav>

          <div className="hidden lg:flex items-center ml-6 space-x-5 whitespace-nowrap">
            <a
              href={LOGIN_URL}
              className="text-sm font-medium text-neutral-600 hover:text-primary-900 transition-colors duration-150"
              data-testid="header-login-link"
            >
              Log In
            </a>
            <Button asChild data-testid="header-request-info-btn">
              <Link to="/request-info">Request Information</Link>
            </Button>
          </div>

          <button
            type="button"
            className="lg:hidden p-2 -mr-2 text-neutral-600 hover:text-primary-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="lg:hidden max-h-[calc(100vh-4rem)] overflow-y-auto bg-white border-t border-neutral-200"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-3">
            {navLinks.map((link) => mobileLink(link.href, link.name, `mobile-nav-link-${slug(link.name)}`))}
            {/* Solutions: the menu's groups, expanded as plain lists */}
            <div className="mt-3 border-t border-neutral-200 pt-3">
              {SOLUTION_GROUPS.map((group) => (
                <div key={group.label} className="pb-2">
                  <p className="label-uppercase pt-2 pb-1 text-neutral-500">{group.label}</p>
                  {group.links.map((link) => mobileLink(link.to, link.label, `mobile-solutions-link-${slug(link.label)}`))}
                </div>
              ))}
              {mobileLink(ALL_USE_CASES.to, ALL_USE_CASES.label, 'mobile-solutions-link-all-use-cases')}
            </div>
            {/* Resources: expanded as a plain list */}
            <div className="mt-3 border-t border-neutral-200 pt-3">
              <p className="label-uppercase pt-2 pb-1 text-neutral-500">{RESOURCES_MENU}</p>
              {RESOURCE_LINKS.map((link) => mobileLink(link.to, link.label, `mobile-resources-link-${slug(link.label)}`))}
            </div>
            <div className="mt-3 pt-3 border-t border-neutral-200 space-y-3">
              <a
                href={LOGIN_URL}
                className="block py-2 text-sm font-medium text-neutral-600"
                data-testid="mobile-login-link"
              >
                Log In
              </a>
              <Button asChild className="w-full" data-testid="mobile-request-info-btn">
                <Link to="/request-info" onClick={() => setMobileMenuOpen(false)}>
                  Request Information
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
